import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useRouteError, isRouteErrorResponse, useLocation } from 'react-router-dom'
import { AlertTriangle, RefreshCw, Mail } from 'lucide-react'

const SUPPORT_EMAIL = 'info@takayama.edu.np'

/** Builds a mailto: href with a pre-filled subject and body describing the error. */
function buildMailtoHref(opts: {
  errorMessage: string
  errorStack?: string
  componentName?: string
  routePath?: string
}): string {
  const { errorMessage, errorStack, componentName, routePath } = opts
  const where = componentName
    ? `Component: ${componentName}`
    : routePath
    ? `Route: ${routePath}`
    : 'Unknown location'

  const subject = encodeURIComponent(`Bug report — ${where}`)
  const body = encodeURIComponent(
    [
      `Hi Takayama support,`,
      ``,
      `I encountered an unexpected error while using the application.`,
      ``,
      `--- Where ---`,
      where,
      ...(routePath && componentName ? [`Route: ${routePath}`] : []),
      ``,
      `--- Error ---`,
      errorMessage,
      ``,
      `--- Stack trace ---`,
      errorStack ?? '(not available)',
      ``,
      `--- Steps to reproduce ---`,
      `1. `,
      `2. `,
      ``,
      `Please let me know if you need more information.`,
    ].join('\n')
  )

  return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`
}

interface Props {
  children: ReactNode
  /** Optional custom fallback. If not provided, a default error UI is shown. */
  fallback?: ReactNode
  /** Optional display name shown in the bug-report email subject. */
  componentName?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * ErrorBoundary — catches uncaught errors in the React tree and renders a
 * graceful fallback UI instead of crashing the whole application.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <SomeFeaturePage />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // In production you would pipe this to a monitoring service (e.g. Sentry).
    console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const error = this.state.error
      const mailtoHref = error
        ? buildMailtoHref({
            errorMessage: error.message,
            errorStack: error.stack,
            componentName: this.props.componentName,
          })
        : `mailto:${SUPPORT_EMAIL}`

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 p-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-gray-900">Something went wrong</h2>
            <p className="text-sm text-gray-500 max-w-sm">
              An unexpected error occurred. You can try again, or report this to our team.
            </p>
            {import.meta.env.DEV && error && (
              <pre className="mt-3 max-w-lg overflow-auto rounded-md bg-red-50 p-3 text-left text-xs text-red-700">
                {error.message}
              </pre>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <RefreshCw className="h-4 w-4" />
              Try again
            </button>
            <a
              href={mailtoHref}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <Mail className="h-4 w-4" />
              Report this bug
            </a>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// ── Route-level error boundary ───────────────────────────────────────────────

/**
 * RouteErrorBoundary — used as React Router's `errorElement`.
 * Catches loader / action / lazy-import errors and shows the same
 * graceful UI with a pre-filled bug-report email link.
 */
export function RouteErrorBoundary() {
  const routeError = useRouteError()
  const location = useLocation()

  let errorMessage = 'Unknown error'
  let errorStack: string | undefined

  if (isRouteErrorResponse(routeError)) {
    errorMessage = `${routeError.status} ${routeError.statusText}`
  } else if (routeError instanceof Error) {
    errorMessage = routeError.message
    errorStack = routeError.stack
  } else if (typeof routeError === 'string') {
    errorMessage = routeError
  }

  const mailtoHref = buildMailtoHref({
    errorMessage,
    errorStack,
    routePath: location.pathname,
  })

  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
        <AlertTriangle className="h-8 w-8 text-red-500" />
      </div>
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-gray-900">Something went wrong</h2>
        <p className="text-sm text-gray-500 max-w-sm">
          An unexpected error occurred while loading this page.
        </p>
        {import.meta.env.DEV && (
          <pre className="mt-3 max-w-lg overflow-auto rounded-md bg-red-50 p-3 text-left text-xs text-red-700">
            {errorMessage}
          </pre>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          <RefreshCw className="h-4 w-4" />
          Reload page
        </button>
        <a
          href={mailtoHref}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          <Mail className="h-4 w-4" />
          Report this bug
        </a>
      </div>
    </div>
  )
}
